<script setup>
import { ref, reactive, onMounted } from 'vue';
import { ElMessage, ElMessageBox } from 'element-plus';
import axios from 'axios';

// ===== 外部型号过滤开关 =====
const externalCheckEnabled = ref(false);
const externalCheckLoading = ref(false);

// 获取外部型号过滤开关状态
const fetchExternalCheckSetting = async () => {
  try {
    const response = await axios.get('/api/v1/model-record/external-check/setting', {
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('token')}`
      }
    });
    if (response.data.code === '200' && response.data.data) {
      externalCheckEnabled.value = response.data.data.enabled === true;
    }
  } catch (error) {
    console.error('获取外部型号过滤开关失败:', error);
  }
};

// 切换外部型号过滤开关（带二次确认）
const handleExternalCheckChange = async (val) => {
  const actionText = val ? '开启' : '关闭';
  const confirmMsg = val
    ? '开启后，下单页型号列表将根据外部商务系统返回数据进行过滤，未授权的型号将不再显示给用户。确定开启吗？'
    : '关闭后，下单页将显示全部型号，不再进行外部过滤。确定关闭吗？';

  try {
    await ElMessageBox.confirm(confirmMsg, `确认${actionText}`, {
      confirmButtonText: '确定',
      cancelButtonText: '取消',
      type: 'warning'
    });
  } catch {
    // 用户点击取消，恢复开关原值
    externalCheckEnabled.value = !val;
    return;
  }

  try {
    externalCheckLoading.value = true;
    const response = await axios.put('/api/v1/model-record/external-check/setting', 
      { enabled: val },
      {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      }
    );
    if (response.data.code === '200') {
      ElMessage.success(`外部型号过滤已${actionText}`);
    } else {
      // 恢复原值
      externalCheckEnabled.value = !val;
      ElMessage.error(response.data.info || '设置失败');
    }
  } catch (error) {
    // 恢复原值
    externalCheckEnabled.value = !val;
    console.error('设置外部型号过滤开关失败:', error);
    ElMessage.error('设置失败，请检查网络连接或权限');
  } finally {
    externalCheckLoading.value = false;
  }
};

// 当前活跃的标签页
const activeTab = ref('regular');

// 型号数据
const regularModels = ref([]);
const fuxiangsanModels = ref([]);

// 加载状态
const loading = ref(false);

// 对话框状态
const dialogVisible = ref(false);
const dialogTitle = ref('');
const isEdit = ref(false);
const formRef = ref(null);

// 表单数据
const formData = reactive({
  id: null,
  model: '',
  orderType: 'regular',
  enabled: false
});

// 表单验证规则
const formRules = {
  model: [
    { required: true, message: '请输入型号名称', trigger: 'blur' },
    { max: 255, message: '型号名称长度不能超过255字符', trigger: 'blur' }
  ]
};


// 订单类型映射
const orderTypeMap = {
  'regular': '超主点订单',
  'fuxiangsan': '其他订单'
};

// 获取型号列表
const fetchModels = async (orderType) => {
  try {
    loading.value = true;
    const response = await axios.get(`/api/v1/model-record/list/${orderType}`, {
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('token')}`
      }
    });
    
    if (response.data.code === '200') {
      if (orderType === 'regular') {
        regularModels.value = response.data.data || [];
      } else {
        fuxiangsanModels.value = response.data.data || [];
      }
    } else {
      ElMessage.error(response.data.info || '获取型号列表失败');
    }
  } catch (error) {
    console.error('获取型号列表失败:', error);
    ElMessage.error('获取型号列表失败，请检查网络连接');
  } finally {
    loading.value = false;
  }
};

// 获取所有型号数据
const fetchAllModels = async () => {
  await Promise.all([
    fetchModels('regular'),
    fetchModels('fuxiangsan')
  ]);
};

// 切换标签页
const handleTabChange = (tabName) => {
  activeTab.value = tabName;
};

// 打开添加对话框
const openAddDialog = () => {
  dialogTitle.value = `添加${orderTypeMap[activeTab.value]}型号`;
  isEdit.value = false;
  formData.id = null;
  formData.model = '';
  formData.orderType = activeTab.value;
  formData.enabled = false;
  dialogVisible.value = true;
};

// 打开编辑对话框
const openEditDialog = (row) => {
  dialogTitle.value = `编辑${orderTypeMap[activeTab.value]}型号`;
  isEdit.value = true;
  formData.id = row.id;
  formData.model = row.model;
  formData.orderType = row.orderType;
  formData.enabled = row.enabled !== undefined ? row.enabled : false;
  dialogVisible.value = true;
};

// 关闭对话框
const closeDialog = () => {
  dialogVisible.value = false;
  formData.id = null;
  formData.model = '';
  formData.orderType = 'regular';
  formData.enabled = false;
};

// 提交表单
const submitForm = async () => {
  // 先验证表单
  if (!formRef.value) {
    ElMessage.error('表单未初始化');
    return;
  }
  
  try {
    // 使用Element Plus的表单验证
    await formRef.value.validate();
    
    const url = isEdit.value ? '/api/v1/model-record/update' : '/api/v1/model-record/add';
    const method = isEdit.value ? 'put' : 'post';
    
    const response = await axios({
      method,
      url,
      data: formData,
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('token')}`
      }
    });
    
    if (response.data.code === '200') {
      ElMessage.success(response.data.info);
      closeDialog();
      await fetchModels(activeTab.value);
    } else {
      ElMessage.error(response.data.info || '操作失败');
    }
  } catch (error) {
    if (error.message) {
      // 表单验证失败
      console.log('表单验证失败');
    } else {
      console.error('提交失败:', error);
      ElMessage.error('操作失败，请检查网络连接');
    }
  }
};

// 删除型号
const deleteModel = async (row) => {
  try {
    await ElMessageBox.confirm(
      `确定要删除型号"${row.model}"吗？`,
      '确认删除',
      {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }
    );
    
    const response = await axios.delete(`/api/v1/model-record/delete/${row.id}`, {
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('token')}`
      }
    });
    
    if (response.data.code === '200') {
      ElMessage.success('删除成功');
      await fetchModels(activeTab.value);
    } else {
      ElMessage.error(response.data.info || '删除失败');
    }
  } catch (error) {
    if (error !== 'cancel') {
      console.error('删除失败:', error);
      ElMessage.error('删除失败，请检查网络连接');
    }
  }
};

// 页面初始化
onMounted(() => {
  fetchAllModels();
  fetchExternalCheckSetting();
});
</script>

<template>
  <div class="model-management-container">
    <div class="page-header">
      <h1 class="page-title">型号管理</h1>
    </div>

    <!-- 外部型号过滤开关 -->
    <div class="external-check-card">
      <div class="external-check-content">
        <div class="external-check-info">
          <span class="external-check-label">外部商务系统型号过滤</span>
          <span class="external-check-desc">
            开启后，下单页型号列表将根据外部商务系统返回的用户可用型号进行过滤；关闭则显示全部型号。
          </span>
        </div>
        <el-switch
          v-model="externalCheckEnabled"
          :loading="externalCheckLoading"
          active-text="已开启"
          inactive-text="已关闭"
          inline-prompt
          style="--el-switch-on-color: #13ce66; --el-switch-off-color: #ff4949;"
          @change="handleExternalCheckChange"
        />
      </div>
    </div>

    <!-- 标签页 -->
    <div class="tabs-container">
      <el-tabs v-model="activeTab" @tab-change="handleTabChange" class="model-tabs">
        <el-tab-pane label="超主点订单型号" name="regular">
          <div class="tab-content">
            <!-- 操作按钮 -->
            <div class="action-bar">
              <el-button type="primary" @click="openAddDialog" :loading="loading">
                <i class="el-icon-plus"></i>
                添加型号
              </el-button>
            </div>

            <!-- 型号列表 -->
            <div class="model-list" v-loading="loading">
              <div v-if="regularModels.length === 0 && !loading" class="empty-state">
                <p>暂无超主点订单型号数据</p>
              </div>
              <div v-else class="model-cards">
                <div 
                  v-for="model in regularModels" 
                  :key="model.id" 
                  class="model-card"
                >
                  <div class="model-info">
                    <h3 class="model-name">{{ model.model }}</h3>
                    <p class="model-type">{{ orderTypeMap[model.orderType] }}</p>
                    <p class="model-status">
                      <el-tag :type="model.enabled ? 'success' : 'info'" size="small">
                        {{ model.enabled ? '已启用正向/反向提醒' : '未启用' }}
                      </el-tag>
                    </p>
                  </div>
                  <div class="model-actions">
                    <el-button 
                      type="primary" 
                      size="small" 
                      @click="openEditDialog(model)"
                    >
                      编辑
                    </el-button>
                    <el-button 
                      type="danger" 
                      size="small" 
                      @click="deleteModel(model)"
                    >
                      删除
                    </el-button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </el-tab-pane>

        <el-tab-pane label="其他订单型号" name="fuxiangsan">
          <div class="tab-content">
            <!-- 操作按钮 -->
            <div class="action-bar">
              <el-button type="primary" @click="openAddDialog" :loading="loading">
                <i class="el-icon-plus"></i>
                添加型号
              </el-button>
            </div>

            <!-- 型号列表 -->
            <div class="model-list" v-loading="loading">
              <div v-if="fuxiangsanModels.length === 0 && !loading" class="empty-state">
                <p>暂无其他订单型号数据</p>
              </div>
              <div v-else class="model-cards">
                <div 
                  v-for="model in fuxiangsanModels" 
                  :key="model.id" 
                  class="model-card"
                >
                  <div class="model-info">
                    <h3 class="model-name">{{ model.model }}</h3>
                    <p class="model-type">{{ orderTypeMap[model.orderType] }}</p>
                    <p class="model-status">
                      <el-tag :type="model.enabled ? 'success' : 'info'" size="small">
                        {{ model.enabled ? '已启用正向/反向提醒' : '未启用' }}
                      </el-tag>
                    </p>
                  </div>
                  <div class="model-actions">
                    <el-button 
                      type="primary" 
                      size="small" 
                      @click="openEditDialog(model)"
                    >
                      编辑
                    </el-button>
                    <el-button 
                      type="danger" 
                      size="small" 
                      @click="deleteModel(model)"
                    >
                      删除
                    </el-button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </el-tab-pane>
      </el-tabs>
    </div>

    <!-- 添加/编辑对话框 -->
    <el-dialog 
      v-model="dialogVisible" 
      :title="dialogTitle" 
      width="500px"
      :close-on-click-modal="false"
      class="model-dialog"
      align-center
      :center="false"
    >
      <el-form 
        :model="formData" 
        :rules="formRules" 
        ref="formRef"
        label-width="80px"
        class="model-form"
      >
        <el-form-item label="型号名称" prop="model">
          <el-input 
            v-model="formData.model" 
            placeholder="请输入型号名称"
            maxlength="255"
            show-word-limit
          />
        </el-form-item>
        <el-form-item label="订单类型">
          <el-input 
            :value="orderTypeMap[formData.orderType]" 
            disabled
          />
        </el-form-item>
        <el-form-item label="启用状态">
          <el-switch
            v-model="formData.enabled"
            active-text="启用"
            inactive-text="不启用"
            inline-prompt
          />
        </el-form-item>
      </el-form>
      
      <template #footer>
        <div class="dialog-footer">
          <el-button @click="closeDialog">取消</el-button>
          <el-button type="primary" @click="submitForm">
            {{ isEdit ? '更新' : '添加' }}
          </el-button>
        </div>
      </template>
    </el-dialog>
  </div>
</template>

<style scoped>
.model-management-container {
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
}

.page-header {
  margin-bottom: 24px;
  padding-bottom: 16px;
  border-bottom: 1px solid #ebeef5;
}

.page-title {
  font-size: 24px;
  font-weight: bold;
  color: #303133;
  margin: 0;
}

/* 外部型号过滤开关卡片 */
.external-check-card {
  background-color: #fff;
  border-radius: 8px;
  box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.1);
  padding: 20px 24px;
  margin-bottom: 20px;
}

.external-check-content {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.external-check-info {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.external-check-label {
  font-size: 16px;
  font-weight: 600;
  color: #303133;
}

.external-check-desc {
  font-size: 13px;
  color: #909399;
  line-height: 1.5;
}

.tabs-container {
  background-color: #fff;
  border-radius: 8px;
  box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.1);
  padding: 20px;
}

.model-tabs {
  width: 100%;
}

.tab-content {
  padding-top: 20px;
}

.action-bar {
  margin-bottom: 20px;
  display: flex;
  justify-content: flex-start;
}

.model-list {
  min-height: 200px;
}

.empty-state {
  text-align: center;
  padding: 60px 20px;
  color: #909399;
  font-size: 16px;
}

.model-cards {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 16px;
  will-change: auto; /* 避免不必要的GPU加速导致的重绘 */
}

.model-card {
  background-color: #f8f9fa;
  border-radius: 8px;
  padding: 20px;
  border: 1px solid #e9ecef;
  transition: box-shadow 0.3s ease, transform 0.3s ease;
  display: flex;
  justify-content: space-between;
  align-items: center;
  contain: layout; /* 优化渲染性能 */
}

.model-card:hover {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  transform: translateY(-2px);
}

.model-info {
  flex: 1;
}

.model-name {
  font-size: 18px;
  font-weight: bold;
  color: #303133;
  margin: 0 0 8px 0;
}

.model-type {
  font-size: 14px;
  color: #606266;
  margin: 0;
}

.model-status {
  margin: 8px 0 0 0;
}

.model-actions {
  display: flex;
  gap: 8px;
}

.model-form {
  padding: 20px 0;
}

.dialog-footer {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
}

/* 平板适配 */
@media screen and (max-width: 768px) and (min-width: 600px) {
  
  .page-title {
    font-size: 20px;
  }
  
  .tabs-container {
    padding: 15px;
  }
  
  .model-cards {
    grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
    gap: 12px;
  }
  
  .model-card {
    padding: 15px;
  }
  
  .model-name {
    font-size: 16px;
  }
  
  .model-dialog {
    width: 90% !important;
  }
}

/* 手机端适配 */
@media screen and (max-width: 600px) {
  
  .page-title {
    font-size: 18px;
    text-align: center;
  }
  
  .tabs-container {
    padding: 10px;
  }
  
  .action-bar {
    margin-bottom: 15px;
  }
  
  .action-bar .el-button {
    width: 100%;
    justify-content: center;
  }
  
  .model-cards {
    grid-template-columns: 1fr;
    gap: 10px;
  }
  
  .model-card {
    padding: 15px;
    flex-direction: column;
    align-items: flex-start;
    gap: 15px;
  }
  
  .model-info {
    width: 100%;
  }
  
  .model-actions {
    width: 100%;
    justify-content: space-between;
  }
  
  .model-actions .el-button {
    flex: 1;
    margin: 0 5px;
  }
  
  .model-form {
    padding: 10px 0;
  }
  
  .dialog-footer {
    display: flex;
    flex-direction: row;
    justify-content: flex-end;
    gap: 12px;
    width: 100%;
  }
  
  .dialog-footer .el-button {
    flex: 0 0 auto;
    min-width: 80px;
  }
  
  .empty-state {
    padding: 30px 20px;
    font-size: 14px;
  }
  
  /* 标签页在手机端的优化 */
  :deep(.el-tabs__header) {
    margin-bottom: 10px;
  }
  
  :deep(.el-tabs__item) {
    font-size: 14px;
    padding: 0 10px;
  }
  
  :deep(.el-tabs__nav-wrap) {
    padding: 0 5px;
  }
}

/* 超小屏幕适配 */
@media screen and (max-width: 320px) {
  
  .page-title {
    font-size: 16px;
  }
  
  .tabs-container {
    padding: 8px;
  }
  
  .model-card {
    padding: 12px;
  }
  
  .model-name {
    font-size: 14px;
  }
  
  .model-type {
    font-size: 12px;
  }
  
  .model-actions .el-button {
    font-size: 12px;
    padding: 5px 10px;
  }
  
  :deep(.el-tabs__item) {
    font-size: 12px;
    padding: 0 8px;
  }
}

/* 加载状态优化 */
.model-list.el-loading-parent--relative {
  min-height: 300px;
}

/* 对话框响应式优化 - 600px以下 */
@media screen and (max-width: 600px) {
  :deep(.model-dialog) {
    width: 90vw !important;
    max-width: 90vw !important;
  }

  :deep(.model-dialog .el-dialog) {
    width: 90vw !important;
    max-width: 90vw !important;
    margin: 5vh auto !important;
    left: 50% !important;
    transform: translateX(-50%) !important;
    position: fixed !important;
    display: flex;
    flex-direction: column;
    max-height: 85vh;
  }

  :deep(.model-dialog .el-dialog__header) {
    padding: 10px 12px;
    text-align: left;
    flex-shrink: 0;
  }

  :deep(.model-dialog .el-dialog__title) {
    font-size: 16px;
  }

  :deep(.model-dialog .el-dialog__body) {
    padding: 12px;
    overflow-y: visible;
    overflow-x: hidden;
    flex-shrink: 1;
  }

  :deep(.model-dialog .el-dialog__footer) {
    padding: 12px;
    display: flex;
    justify-content: center;
    gap: 10px;
    flex-shrink: 0;
  }

  /* 表单垂直布局 - 除了启用状态 */
  :deep(.model-dialog .el-form-item) {
    flex-direction: column;
    align-items: flex-start;
    margin-bottom: 12px;
  }

  /* 启用状态保持水平布局 */
  :deep(.model-dialog .el-form-item:has(.el-switch)) {
    flex-direction: row !important;
    align-items: center !important;
    justify-content: flex-start !important;
    margin-bottom: 12px;
  }

  :deep(.model-dialog .el-form-item__label) {
    font-size: 13px;
    width: 100% !important;
    margin-bottom: 5px;
    line-height: 2.4;
    display: block !important
  }


  :deep(.model-dialog .el-form-item__content) {
    margin-left: 0 !important;
    width: 100%;
  }


  :deep(.model-dialog .el-input) {
    width: 100%;
  }

  :deep(.model-dialog .el-input__inner) {
    font-size: 13px;
    height: 36px;
    width: 100% !important;
    padding: 0 10px;
  }

  /* 开关样式优化 - 紧凑型 */
  :deep(.model-dialog .el-switch) {
    display: inline-flex;
    align-items: center;
    margin: 0;
  }

  :deep(.model-dialog .el-switch__label) {
    font-size: 12px;
    margin-left: 6px;
    margin-right: 0;
    white-space: nowrap;
  }

  :deep(.model-dialog .el-switch__label--left) {
    margin-right: 6px;
    margin-left: 0;
  }

  /* 字数统计样式 */
  :deep(.model-dialog .el-input__count) {
    font-size: 11px;
  }

  .dialog-footer {
    display: flex;
    flex-direction: row;
    justify-content: center;
    gap: 10px;
    width: 100%;
  }

  .dialog-footer .el-button {
    flex: 0 0 auto;
    min-width: 90px;
    margin: 0;
    font-size: 13px;
    padding: 8px 15px;
  }
}

/* 对话框响应式优化 - 480px以下 */
@media screen and (max-width: 480px) {
  :deep(.model-dialog .el-dialog) {
    width: 95vw !important;
    max-width: 95vw !important;
    margin: 8vh auto !important;
  }

  :deep(.model-dialog .el-dialog__header) {
    padding: 10px 12px;
  }


  :deep(.model-dialog .el-dialog__footer) {
    padding: 10px 12px;
  }

  /* 保持垂直布局 - 除了启用状态 */
  :deep(.model-dialog .el-form-item) {
    margin-bottom: 16px;
  }

  /* 启用状态保持水平布局 */
  :deep(.model-dialog .el-form-item:has(.el-switch)) {
    flex-direction: row;
    align-items: center;
    justify-content: flex-start;
    margin-bottom: 16px;
  }

  :deep(.model-dialog .el-form-item__label) {
    font-size: 13px;
    width: 100% !important;
    margin-bottom: 6px;
    text-align: left !important;
  }

  /* 启用状态标签样式 */
  :deep(.model-dialog .el-form-item:has(.el-switch) .el-form-item__label) {
    width: auto !important;
    margin-bottom: 0;
    margin-right: 10px;
    text-align: left !important;
    flex-shrink: 0;
  }

  :deep(.model-dialog .el-form-item__content) {
    margin-left: 0 !important;
    width: 100%;
  }

  /* 启用状态内容区域 */
  :deep(.model-dialog .el-form-item:has(.el-switch) .el-form-item__content) {
    margin-left: 0 !important;
    width: auto;
    flex: 0 0 auto;
  }

  :deep(.model-dialog .el-input__inner) {
    font-size: 13px;
    width: 100% !important;
  }

  /* 开关样式优化 - 小屏幕 */
  :deep(.model-dialog .el-switch__label) {
    font-size: 13px;
    margin-left: 6px;
    white-space: nowrap;
  }

  :deep(.model-dialog .el-switch__label--left) {
    margin-right: 6px;
    margin-left: 0;
  }

  .dialog-footer {
    flex-direction: column;
    gap: 8px;
    width: 100%;
  }

  .dialog-footer .el-button {
    width: 100%;
    min-width: 0;
  }
}
</style>